import { z } from 'zod';

/**
 * DTOs for the client scan-intake route domain (RUF-499).
 *
 * POST /api/client/scans            — scanCreateBodySchema
 * POST /api/client/scans/upload-url — scanUploadUrlBodySchema
 * POST /api/client/scans/:id/ocr    — scanOcrBodySchema
 * POST /api/client/scans/:id/select — scanSelectBodySchema
 *
 * The match envelope (scanMatchSchema) is the customer-facing three-tier
 * matching result. `skuId`/`candidates[].skuId` are present for form wiring
 * only — UI copy decisions key on `tier`, never on the ids.
 */

// ---------------------------------------------------------------------------
// Request bodies
// ---------------------------------------------------------------------------

export const scanCreateBodySchema = z.object({
  /** Raw decoded barcode value or manually typed identifier. */
  decodedValue: z.string().trim().min(1).max(128),
  /** OCR captures go through POST /scans/:id/ocr, not this endpoint. */
  captureMethod: z.enum(['barcode', 'manual']),
});
export type ScanCreateBody = z.infer<typeof scanCreateBodySchema>;

export const scanUploadUrlBodySchema = z.object({
  /**
   * Existing scan to attach the photo to. Omitted = a fresh UUID key is
   * minted (photo-first flows create the scan at the /ocr step).
   */
  scanId: z.string().uuid().optional(),
});
export type ScanUploadUrlBody = z.infer<typeof scanUploadUrlBodySchema>;

export const scanOcrBodySchema = z.object({
  /**
   * S3 key returned by /scans/upload-url. Defaults to the scan's canonical
   * key (scans/{customerOrgId}/{scanId}.jpg). Must stay inside the caller's
   * org prefix — the route rejects foreign keys.
   */
  imageKey: z.string().min(1).max(512).optional(),
});
export type ScanOcrBody = z.infer<typeof scanOcrBodySchema>;

export const scanSelectBodySchema = z.union([
  /** Pick-list selection: bind the scan to an existing catalog SKU. */
  z.object({ skuId: z.string().uuid() }),
  /** "None of these" → create-or-attach a sku_draft from the scan's lookup data. */
  z.object({ none: z.literal(true) }),
]);
export type ScanSelectBody = z.infer<typeof scanSelectBodySchema>;

// ---------------------------------------------------------------------------
// Match envelope (three-tier matching result)
// ---------------------------------------------------------------------------

export const scanMatchCandidateSchema = z.object({
  skuId: z.string().uuid(),
  modelName: z.string().nullable(),
  manufacturer: z.string().nullable(),
});
export type ScanMatchCandidate = z.infer<typeof scanMatchCandidateSchema>;

const scanMatchCommonFields = {
  manufacturer: z.string().nullable(),
  model: z.string().nullable(),
  serial: z.string().nullable(),
};

export const scanMatchSchema = z.discriminatedUnion('tier', [
  z.object({
    tier: z.literal('exact'),
    ...scanMatchCommonFields,
    skuId: z.string().uuid(),
  }).strict(),
  z.object({
    tier: z.literal('fuzzy'),
    ...scanMatchCommonFields,
    /** Top-3 pick-list candidates. */
    candidates: z.array(scanMatchCandidateSchema),
  }).strict(),
  z.object({
    tier: z.literal('none'),
    ...scanMatchCommonFields,
    /** true when /select {none:true} can create a draft. */
    draftPending: z.boolean(),
  }).strict(),
]);
export type ScanMatch = z.infer<typeof scanMatchSchema>;

export const scanCreateResponseSchema = z.object({
  scanId: z.string().uuid(),
  match: scanMatchSchema,
});
export type ScanCreateResponse = z.infer<typeof scanCreateResponseSchema>;
